import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import { Card } from '../ui/Card';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Badge from '../ui/Badge';
import { blogService, BlogPost, BlogConnection } from '../../services/blogService';
import { Plus, Search, Edit2, Trash2, Send, ExternalLink, FileText, Calendar, Loader2, Globe } from 'lucide-react';
import BlogPostEditor from './BlogPostEditor';

// ==================== Helpers ====================

type TFunc = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

function kindOptions(t: TFunc) {
  return [
    { value: 'article', label: t('blogSeo.posts.kindArticle', 'Artigo') },
    { value: 'news', label: t('blogSeo.posts.kindNews', 'Notícia') },
    { value: 'tutorial', label: t('blogSeo.posts.kindTutorial', 'Tutorial') },
    { value: 'listicle', label: t('blogSeo.posts.kindListicle', 'Lista') },
    { value: 'review', label: t('blogSeo.posts.kindReview', 'Review') },
  ];
}

function kindLabel(t: TFunc, kind: string): string {
  const map: Record<string, string> = {
    article: t('blogSeo.posts.kindArticle', 'Artigo'),
    news: t('blogSeo.posts.kindNews', 'Notícia'),
    tutorial: t('blogSeo.posts.kindTutorial', 'Tutorial'),
    listicle: t('blogSeo.posts.kindListicle', 'Lista'),
    review: t('blogSeo.posts.kindReview', 'Review'),
  };
  return map[kind] ?? kind;
}

function statusLabel(t: TFunc, status: string): string {
  const map: Record<string, string> = {
    draft: t('blogSeo.posts.statusDraft', 'Rascunho'),
    scheduled: t('blogSeo.posts.statusScheduled', 'Agendado'),
    publishing: t('blogSeo.posts.statusPublishing', 'Publicando'),
    published: t('blogSeo.posts.statusPublished', 'Publicado'),
    failed: t('blogSeo.posts.statusFailed', 'Falhou'),
    archived: t('blogSeo.posts.statusArchived', 'Arquivado'),
  };
  return map[status] ?? status;
}

const STATUS_VARIANT: Record<string, 'primary' | 'success' | 'warning' | 'danger' | 'gray'> = {
  draft: 'gray',
  scheduled: 'warning',
  publishing: 'primary',
  published: 'success',
  failed: 'danger',
  archived: 'gray',
};

function formatDate(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR');
}

function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-500';
  if (score >= 50) return 'text-yellow-500';
  return 'text-red-500';
}

function scoreRing(score: number): string {
  if (score >= 80) return 'border-green-500';
  if (score >= 50) return 'border-yellow-500';
  return 'border-red-500';
}

// ==================== Post Card ====================


interface PostCardProps {
  post: BlogPost;
  onEdit: (post: BlogPost) => void;
  onPublish: (post: BlogPost) => void;
  onDelete: (post: BlogPost) => void;
  publishingId: string | null;
}

const PostCard: React.FC<PostCardProps> = ({ post, onEdit, onPublish, onDelete, publishingId }) => {
  const { t } = useTranslation();
  const dateLabel =
    post.status === 'published'
      ? formatDate(post.published_at)
      : post.status === 'scheduled'
      ? formatDate(post.scheduled_at)
      : formatDate(post.created_at);

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800/50 p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="gray">{kindLabel(t, post.kind)}</Badge>
          <Badge variant={STATUS_VARIANT[post.status] ?? 'gray'}>{statusLabel(t, post.status)}</Badge>
        </div>
        <div
          className={`flex-shrink-0 rounded-full border-2 ${scoreRing(post.seo_score)} w-11 h-11 flex items-center justify-center text-sm font-bold ${scoreColor(
            post.seo_score,
          )}`}
          title="SEO score"
        >
          {post.seo_score}
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-2">{post.title}</h3>
        {post.excerpt && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{post.excerpt}</p>}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
        <span className="inline-flex items-center gap-1">
          <FileText className="w-3.5 h-3.5" />
          {t('blogSeo.posts.wordCount', '{{count}} palavras', { count: post.word_count })}
        </span>
        {post.connection?.name && (
          <span className="inline-flex items-center gap-1">
            <Globe className="w-3.5 h-3.5" />
            {post.connection.name}
          </span>
        )}
        {dateLabel && (
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {dateLabel}
          </span>
        )}
      </div>

      {post.status === 'failed' && post.error_message && (
        <p className="text-xs text-red-500 line-clamp-2">{post.error_message}</p>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-1 mt-auto">
        <Button variant="secondary" size="sm" leftIcon={<Edit2 className="w-3.5 h-3.5" />} onClick={() => onEdit(post)}>
          {t('blogSeo.posts.edit', 'Editar')}
        </Button>
        {post.status !== 'published' && (
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Send className="w-3.5 h-3.5" />}
            onClick={() => onPublish(post)}
            isLoading={publishingId === post.id}
          >
            {t('blogSeo.posts.publish', 'Publicar')}
          </Button>
        )}
        {post.external_url && (
          <a href={post.external_url} target="_blank" rel="noopener noreferrer">
            <Button variant="ghost" size="sm" leftIcon={<ExternalLink className="w-3.5 h-3.5" />}>
              {t('blogSeo.posts.open', 'Abrir')}
            </Button>
          </a>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="text-red-500 hover:text-red-600"
          leftIcon={<Trash2 className="w-3.5 h-3.5" />}
          onClick={() => onDelete(post)}
        >
          {t('blogSeo.posts.delete', 'Excluir')}
        </Button>
      </div>
    </div>
  );
};

// ==================== Main Component ====================

const BlogPosts: React.FC = () => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [connections, setConnections] = useState<BlogConnection[]>([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);

  // Debounce search
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(handle);
  }, [search]);

  const loadConnections = useCallback(async () => {
    try {
      const res = await blogService.listConnections();
      setConnections(res?.data ?? []);
    } catch {
      // non-critical
    }
  }, []);

  const loadPosts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await blogService.listPosts({
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
        kind: kindFilter || undefined,
        page: 1,
        limit: 50,
      });
      setPosts(res?.data ?? []);
    } catch (err: any) {
      toast.error((err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message) || t('blogSeo.posts.toastLoadError', 'Erro ao carregar posts'));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter, kindFilter]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  // location.key muda também quando o botão "Nova postagem" do topo da página salva e
  // volta para /blog-seo/posts: recarrega a lista para o post novo aparecer.
  const locationKey = useLocation().key;
  useEffect(() => {
    loadPosts();
  }, [loadPosts, locationKey]);

  const openNew = () => {
    setEditingPost(null);
    setEditorOpen(true);
  };

  const openEdit = (post: BlogPost) => {
    setEditingPost(post);
    setEditorOpen(true);
  };

  const handlePublish = async (post: BlogPost) => {
    const connId = post.connection_id ?? undefined;
    if (!connId) {
      toast.error(t('blogSeo.posts.toastNoConnection', 'Este post não tem conexão. Edite-o e selecione uma conexão.'));
      return;
    }
    try {
      setPublishingId(post.id);
      await blogService.publishPost(post.id, connId);
      toast.success(t('blogSeo.posts.toastPublishStarted', 'Publicação iniciada'));
      loadPosts();
    } catch (err: any) {
      toast.error((err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message) || t('blogSeo.posts.toastPublishError', 'Erro ao publicar'));
    } finally {
      setPublishingId(null);
    }
  };

  const handleDelete = async (post: BlogPost) => {
    if (!window.confirm(t('blogSeo.posts.confirmDelete', 'Excluir o post "{{title}}"?', { title: post.title }))) return;
    try {
      await blogService.deletePost(post.id);
      toast.success(t('blogSeo.posts.toastPostDeleted', 'Post excluído'));
      loadPosts();
    } catch (err: any) {
      toast.error((err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message) || t('blogSeo.posts.toastDeleteError', 'Erro ao excluir'));
    }
  };

  const statusOptions = [
    { value: '', label: t('blogSeo.posts.filterAllStatus', 'Todos os status') },
    { value: 'draft', label: t('blogSeo.posts.statusDraft', 'Rascunho') },
    { value: 'scheduled', label: t('blogSeo.posts.statusScheduled', 'Agendado') },
    { value: 'published', label: t('blogSeo.posts.statusPublished', 'Publicado') },
    { value: 'failed', label: t('blogSeo.posts.statusFailed', 'Falhou') },
  ];

  const kindFilterOptions = [{ value: '', label: t('blogSeo.posts.filterAllKinds', 'Todos os tipos') }, ...kindOptions(t)];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Sem título/botão próprios: o topo da página já tem "Blog e Google" e "Nova postagem" */}
      <p className="text-sm text-gray-500 dark:text-gray-400 -mt-2">
        {t('blogSeo.posts.pageSubtitle', 'Crie, edite e publique conteúdo com análise SEO em tempo real.')}
      </p>

      {/* Filters */}
      <Card>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4 [&>*:first-child]:col-span-2 md:[&>*:first-child]:col-span-1">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('blogSeo.posts.searchPlaceholder', 'Buscar posts...')}
            leftIcon={<Search className="w-4 h-4" />}
          />
          <Select options={statusOptions} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} />
          <Select options={kindFilterOptions} value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} />
        </div>
      </Card>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          {t('blogSeo.posts.loadingPosts', 'Carregando posts...')}
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
          <FileText className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600" />
          <p className="mt-3 text-gray-900 dark:text-white font-medium">{t('blogSeo.posts.emptyTitle', 'Nenhum post encontrado')}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('blogSeo.posts.emptySubtitle', 'Crie seu primeiro post para começar.')}</p>
          <div className="mt-4 flex justify-center">
            <Button variant="primary" leftIcon={<Plus className="w-4 h-4" />} onClick={openNew}>
              {t('blogSeo.posts.newPost', 'Novo post')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              onEdit={openEdit}
              onPublish={handlePublish}
              onDelete={handleDelete}
              publishingId={publishingId}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <BlogPostEditor
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        post={editingPost}
        connections={connections}
        onSaved={loadPosts}
      />
    </div>
  );
};

export default BlogPosts;
